import React from 'react';
import { Info, Droplet, Brain, Heart, Zap, AlertTriangle, ThermometerSun } from 'lucide-react';

/**
 * Компонент с информацией о важности воды для организма
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о воде: польза, нормы, рекомендации
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Droplet className="w-5 h-5 text-blue-500" />
          Почему вода так важна для организма?
        </h3>
        <p>
          <strong>Вода</strong> – основа жизни и самый важный элемент для функционирования человеческого организма. 
          Она составляет около 60% массы тела взрослого человека и участвует практически во всех процессах жизнедеятельности:
        </p>
        <ul>
          <li>Транспортирует питательные вещества к клеткам</li>
          <li>Выводит продукты жизнедеятельности и токсины</li>
          <li>Регулирует температуру тела через потоотделение</li>
          <li>Участвует в пищеварении и усвоении питательных веществ</li>
          <li>Защищает и смазывает суставы, органы и ткани</li>
          <li>Помогает преобразовывать пищу в энергию</li>
          <li>Улучшает кровообращение и транспорт кислорода</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-indigo-500" />
          Влияние воды на мозг и когнитивные функции
        </h3>
        <p>
          Мозг состоит из воды на 75-85%, поэтому даже легкое обезвоживание может оказать значительное влияние на его работу. 
          Исследования показывают, что потеря воды всего на 1-2% от массы тела может привести к:
        </p>
        <ul>
          <li>Снижению концентрации внимания</li>
          <li>Ухудшению кратковременной памяти</li>
          <li>Замедлению скорости реакции</li>
          <li>Ухудшению настроения и повышению тревожности</li>
          <li>Головным болям и мигреням</li>
          <li>Чувству усталости и сонливости</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-500" />
          Вода и здоровье сердечно-сосудистой системы
        </h3>
        <p>
          Достаточное потребление воды имеет огромное значение для здоровья сердца и сосудов. Адекватная гидратация:
        </p>
        <ul>
          <li>Помогает поддерживать оптимальную вязкость крови, снижая нагрузку на сердце</li>
          <li>Способствует нормализации артериального давления</li>
          <li>Уменьшает риск образования тромбов</li>
          <li>Повышает эластичность сосудов</li>
        </ul>
        <p>
          По данным исследований, люди, потребляющие достаточное количество воды, имеют меньший риск развития сердечной недостаточности.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Рекомендации Министерства здравоохранения РФ</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Согласно официальным рекомендациям Министерства здравоохранения РФ и НИИ питания РАМН, для взрослого человека рекомендуется 
            потреблять около 30 мл воды на 1 кг массы тела. При физических нагрузках и в жаркую погоду эта норма 
            увеличивается до 35-40 мл на 1 кг.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Признаки обезвоживания
        </h3>
        <p>
          Обезвоживание может наступить незаметно, поэтому важно знать основные признаки нехватки воды в организме:
        </p>
        <ul>
          <li><strong>Жажда</strong> – первый и самый очевидный сигнал (важно: пожилые люди могут не чувствовать жажду даже при обезвоживании)</li>
          <li><strong>Сухость во рту</strong> и липкая слюна</li>
          <li><strong>Темно-желтая моча</strong> и уменьшение ее количества</li>
          <li><strong>Сухость кожи</strong>, снижение эластичности</li>
          <li><strong>Головная боль</strong>, головокружение</li>
          <li><strong>Усталость</strong>, снижение работоспособности</li>
          <li><strong>Спазмы в мышцах</strong>, особенно в ногах</li>
          <li><strong>Учащенное сердцебиение</strong> и дыхание</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Когда нужно обратиться к врачу
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Сильная жажда, которую трудно утолить</li>
            <li>Очень редкое мочеиспускание (менее 3-4 раз в день)</li>
            <li>Крайне темная моча</li>
            <li>Сильная головная боль и головокружение</li>
            <li>Спутанность сознания, раздражительность</li>
            <li>Учащенное сердцебиение в состоянии покоя</li>
            <li>Высокая температура тела</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <ThermometerSun className="w-5 h-5 text-orange-500" />
          Особенности потребления воды в разных условиях
        </h3>
        <h4>В жаркую погоду</h4>
        <p>
          В жаркую погоду организм теряет больше влаги через потоотделение. Рекомендации:
        </p>
        <ul>
          <li>Увеличить потребление воды на 500-1000 мл</li>
          <li>Пить воду небольшими порциями, но чаще</li>
          <li>Носить с собой бутылку воды</li>
          <li>Употреблять прохладную, но не ледяную воду</li>
          <li>Избегать напитков с кофеином и алкоголем, которые усиливают обезвоживание</li>
        </ul>

        <h4>При физических нагрузках</h4>
        <p>
          Во время интенсивных тренировок организм может терять до 1-1,5 литров воды в час через повышенное потоотделение. Рекомендации:
        </p>
        <ul>
          <li>Выпивать 400-600 мл воды за 2 часа до тренировки</li>
          <li>Во время тренировки пить по 150-250 мл каждые 15-20 минут</li>
          <li>После тренировки восполнить потерю жидкости из расчета 1,5 л воды на каждый килограмм потерянного веса</li>
          <li>При тренировках длительностью более часа добавлять в воду электролиты для восполнения минералов</li>
        </ul>

        <h4>В холодное время года</h4>
        <p>
          Зимой люди часто пьют меньше воды, поскольку не ощущают жажду так остро, как в жаркую погоду. При этом:
        </p>
        <ul>
          <li>В отапливаемых помещениях воздух сухой, что увеличивает потерю влаги через дыхание и кожу</li>
          <li>Тёплая одежда повышает потоотделение даже в холодную погоду</li>
          <li>При дыхании холодным воздухом теряется больше влаги</li>
        </ul>
        <p>
          В холодное время года рекомендуется поддерживать тот же уровень потребления воды, что и в теплое время, 
          дополняя его теплыми напитками (например, теплой водой с лимоном, травяными чаями).
        </p>

        <h3>Особенности потребления воды для разных групп людей</h3>
        <h4>Беременные и кормящие женщины</h4>
        <p>
          Потребность в воде увеличивается:
        </p>
        <ul>
          <li>Во время беременности – дополнительно 300 мл в день</li>
          <li>При грудном вскармливании – дополнительно 700-800 мл в день</li>
        </ul>

        <h4>Пожилые люди</h4>
        <p>
          С возрастом механизм жажды ослабевает, увеличивается риск обезвоживания. Рекомендации:
        </p>
        <ul>
          <li>Пить воду по режиму, а не по желанию</li>
          <li>Начинать день со стакана воды</li>
          <li>Иметь всегда под рукой бутылку с водой</li>
          <li>Употреблять больше продуктов с высоким содержанием воды (овощи, фрукты)</li>
        </ul>

        <h4>Дети</h4>
        <p>
          Потребность в воде у детей относительно массы тела выше, чем у взрослых. Примерные рекомендации:
        </p>
        <ul>
          <li>1-3 года: 1,3 литра в день (включая воду из пищи)</li>
          <li>4-8 лет: 1,7 литра в день (включая воду из пищи)</li>
          <li>9-13 лет: 2,1 литра для девочек и 2,4 литра для мальчиков</li>
          <li>14-18 лет: 2,3 литра для девушек и 3,3 литра для юношей</li>
        </ul>

        <h3>Советы для поддержания оптимального водного баланса</h3>
        <ol>
          <li><strong>Начинайте день со стакана воды</strong>. После ночного сна организм нуждается в регидратации.</li>
          <li><strong>Носите с собой бутылку воды</strong>. Это напомнит вам пить регулярно в течение дня.</li>
          <li><strong>Устанавливайте напоминания</strong>. Используйте приложения или настройте уведомления, чтобы не забывать о приеме воды.</li>
          <li><strong>Пейте воду до, во время и после приема пищи</strong>. Стакан воды за 30 минут до еды помогает контролировать аппетит и улучшает пищеварение.</li>
          <li><strong>Употребляйте продукты с высоким содержанием воды</strong>: огурцы (96% воды), сельдерей (95%), помидоры (94%), арбуз (92%), клубника (91%).</li>
          <li><strong>Разнообразьте вкус воды</strong>. Добавляйте лимон, огурец, мяту или другие натуральные ароматизаторы без сахара.</li>
          <li><strong>Ограничьте потребление кофе, крепкого чая и алкоголя</strong>, которые обладают мочегонным эффектом.</li>
          <li><strong>Следите за цветом мочи</strong>. Светло-соломенный цвет указывает на хорошую гидратацию.</li>
        </ol>

        <p className="font-semibold mt-6">
          Регулярное и достаточное потребление воды – одна из самых простых и эффективных привычек для поддержания здоровья 
          и профилактики многих заболеваний. Используйте наш калькулятор, чтобы определить оптимальную дневную норму 
          именно для вас с учетом всех индивидуальных факторов!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;